import {Time, formatDate} from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {Component, ElementRef, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, map} from 'rxjs';
import {environment} from '../../environments/environment';
import {compareTimeslotStart, compareTimeslotStartEnd} from '../components/Utils';
import {RowEntry, UserErrors} from '../models';
import {GeneralconfigPublicService} from '../service/generalconfig-public.service';
import {MiniBookAdminService} from '../service/mini-book-admin.service';
import {RoomPublicService} from '../service/room-public.service';
import {TimeslotPublicService} from '../service/timeslot-public.service';
import {TimetableEntryPublicService} from '../service/timetable-entry-public.service';
import {AdditionalField} from '../shared/AdditionalField';
import {AdditionalFieldData} from '../shared/AdditionalFieldData';
import {Room} from '../shared/Room';
import {Timeslot} from '../shared/Timeslot';
import {TagCategory} from '../shared/TagCategory';
import {Tag} from '../shared/Tag';
import {TagsPublicService} from '../service/tags-public.service';

type RowEntryTimeslotCount = RowEntry & {timeslotCount?: number};

@Component({
    selector: 'edu-udo-itmc-dev-mini-book-application',
    templateUrl: './application.component.html',
    styleUrls: ['./application.component.css'],
    providers: [
        GeneralconfigPublicService,
        TimeslotPublicService,
        RoomPublicService,
        TimetableEntryPublicService
    ],
    standalone: false
})
export class ApplicationComponent implements OnInit {
  sentinalNullValue = AdditionalFieldData.SENTINAL_NULL_VALUE;

  generalProblem = "";

  salutation?: string;
  lastname = "";
  firstname = "";
  email = "";
  emailRepeated = "";
  rules: Rule[] = [];

  titleMessage = "";
  bookingMessage = "";
  applicationSuccessfulMessage = '<p>Sie haben sich erfolgreich angemeldet!</p>';

  beforeBookingStartMessage = '';
  afterBookingEndMessage = '';
  bookedUpMessage = '';

  additionalFieldsConfig: AdditionalField[] = [];
  additionalFields: AdditionalFieldData[] = [];

  timetableEntries: RowEntry[] = [];
  groupedTimetable: GroupedEntry[] = [];
  errors?: UserErrors;
  applicationSuccessful = false;
  groupedTimetableExpanded: boolean[] = [];
  groupedByOptions: {label: string, value: string[]}[] = [];
  groupedBy: string[] = [];
  searchTerm: string = '';

  tagCategories: TagCategory[] = [];
  tags: {[id: number]: Tag} = {};
  showTags: boolean = false;
  selectedTags: {[catId: number]: number[]} = {};

  requestRunning = false;

  page: number = 0;  

  applicationTitle: string = "Anmeldung";

  privacyPolicyText?: string;
  privacyPolicyUrl?: string;
  privacyPolicyChecked: boolean = false;

  showTypeInTable: boolean = false;
  splitApplicationForm: boolean = false;
  showSalutationField: boolean = false;

  constructor(
    private translate: TranslateService,
    private roomService: RoomPublicService,
    private timeslotService: TimeslotPublicService,
    private timetableService: TimetableEntryPublicService,
    private tagsService: TagsPublicService,
    private generalconfigService: GeneralconfigPublicService,
    private adminService: MiniBookAdminService,
    private elementRef: ElementRef,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.adminService.tenant = this.elementRef.nativeElement.getAttribute('tenant');
    this.updateData();
  }

  private updateData() {
    forkJoin([
      this.roomService.getRooms(),
      this.timeslotService.getTimeslots(),
      this.timetableService.getTimetableEntries(),
      this.tagsService.getTags(),
      this.tagsService.getTagCategories(),
      this.generalconfigService.getGeneralConfig()]
    ).subscribe(([roomsResult, timeslotsResult, timetableEntries, tags, tagCategories, generalConfig]) => {
      const rooms: {[key: number]: Room} = {};
      roomsResult.forEach(r => {rooms[r.id] = r;});
      const timeslots: {[key: number]: Timeslot} = {};
      timeslotsResult.forEach(t => {timeslots[t.id] = t;});
      const selectedEntries = this.timetableEntries
        .filter(re => re.selected)
        .map(re => re.entry.id);

      this.timetableEntries = timetableEntries.map((e) => {
        return {
          selected: (selectedEntries.indexOf(e.id) >= 0 && (e.attendeeCount === undefined || e.attendeeCount < e.maxAttendees || e.maxAttendees == 0)) || e.type === 'BREAK',
          entry: e,
          room: rooms[e.roomId],
          timeslot: timeslots[e.timeslotId]
        };
      });
      this.timetableEntries.sort((a, b) => {
        const tsCompare = compareTimeslotStart(a.timeslot, b.timeslot);
        if (tsCompare != 0) {
          return tsCompare;
        } else {
          const valA = a.entry.title ? a.entry.title.trim() : '';
          const valB = b.entry.title ? b.entry.title.trim() : '';
          return valA.localeCompare(valB);
        }
      });
      if (generalConfig.registerStart && this.isAfterToday(generalConfig.registerStart)) {
        if (generalConfig.beforeRegisterStartMessage && this.hasVisibleText(generalConfig.beforeRegisterStartMessage)) {
          this.generalProblem = generalConfig.beforeRegisterStartMessage;
        } else {
          this.generalProblem = `Die Anmeldung ist ab dem ${formatDate(generalConfig.registerStart, 'longDate', 'de')} möglich.`;
        }
      } else if (generalConfig.registerEnd && this.isBeforeToday(generalConfig.registerEnd)) {
        if (generalConfig.afterRegisterEndMessage && this.hasVisibleText(generalConfig.afterRegisterEndMessage)) {
          this.generalProblem = generalConfig.afterRegisterEndMessage;
        } else {
          this.generalProblem = `Die Anmeldung war leider nur bis zum ${formatDate(generalConfig.registerEnd, 'longDate', 'de')} möglich.`;
        }
      } else if (generalConfig.attendeeCount && generalConfig.maxTotalAttendeesCount && generalConfig.attendeeCount >= generalConfig.maxTotalAttendeesCount) {
        if (generalConfig.bookedUpMessage && this.hasVisibleText(generalConfig.bookedUpMessage)) {
          this.generalProblem = generalConfig.bookedUpMessage;
        } else {
          this.generalProblem = "Leider sind wir ausgebucht. Versuch es nächstes Jahr wieder.";
        }
      }

      if (generalConfig.applicationTitle) {
        this.applicationTitle = generalConfig.applicationTitle;
      }
      if (generalConfig.workshopName) {
        this.translate.set('WORKSHOP', generalConfig.workshopName);
      }
      if (generalConfig.infoName) {
        this.translate.set('INFO', generalConfig.infoName);
      }
      if (generalConfig.breakName) {
        this.translate.set('BREAK', generalConfig.breakName);
      }

      this.rules = [];
      forkJoin([
        this.translate.get('INFO'),
        this.translate.get('WORKSHOP')
      ]).subscribe(names => {
        if (generalConfig.minCountInfo || generalConfig.maxCountInfo) {
          if (generalConfig.minCountInfo === generalConfig.maxCountInfo) {
            let message = `${names[0]}: Genau <strong>${generalConfig.minCountInfo}</strong> auswählen.`;
            this.rules.push(new Rule(message, 'INFO', generalConfig.minCountInfo, generalConfig.minCountInfo));
          } else {
            if (generalConfig.minCountInfo) {
              let message = `${names[0]}: Mindestens <strong>${generalConfig.minCountInfo}</strong> auswählen.`;
              this.rules.push(new Rule(message, 'INFO', generalConfig.minCountInfo, undefined));
            }
            if (generalConfig.maxCountInfo) {
              let message = `${names[0]}: Maximal <strong>${generalConfig.maxCountInfo}</strong> auswählen.`;
              this.rules.push(new Rule(message, 'INFO', undefined, generalConfig.maxCountInfo));
            }
          }
        }
        if (generalConfig.minCountWorkshop || generalConfig.maxCountWorkshop) {
          if (generalConfig.minCountWorkshop === generalConfig.maxCountWorkshop) {
            let message = `${names[1]}: Genau <strong>${generalConfig.minCountWorkshop}</strong> auswählen.`;
            this.rules.push(new Rule(message, 'WORKSHOP', generalConfig.minCountWorkshop, generalConfig.minCountWorkshop));
          } else {
            if (generalConfig.minCountWorkshop) {
              let message = `${names[1]}: Mindestens <strong>${generalConfig.minCountWorkshop}</strong> auswählen.`;
              this.rules.push(new Rule(message, 'WORKSHOP', generalConfig.minCountWorkshop, undefined));
            }
            if (generalConfig.maxCountWorkshop) {
              let message = `${names[1]}: Maximal <strong>${generalConfig.maxCountWorkshop}</strong> auswählen.`;
              this.rules.push(new Rule(message, 'WORKSHOP', undefined, generalConfig.maxCountWorkshop));
            }
          }
        }
      });
      if (generalConfig.titleMessage && this.hasVisibleText(generalConfig.titleMessage)) {
        this.titleMessage = generalConfig.titleMessage;
      }
      if (generalConfig.bookingMessage && this.hasVisibleText(generalConfig.bookingMessage)) {
        this.bookingMessage = generalConfig.bookingMessage;
      }
      if (generalConfig.applicationSuccessfulMessage && this.hasVisibleText(generalConfig.applicationSuccessfulMessage)) {
        this.applicationSuccessfulMessage = generalConfig.applicationSuccessfulMessage;
      }
      if (generalConfig.additionalFields) {
        this.additionalFieldsConfig = generalConfig.additionalFields;
        if (this.additionalFields.length == 0) {
          this.additionalFieldsConfig.forEach(field => {
            this.additionalFields.push(new AdditionalFieldData(field.id, field.type));
          });
        }
      }
      if (generalConfig.privacyPolicyText) {
        this.privacyPolicyText = generalConfig.privacyPolicyText;
      }
      if (generalConfig.privacyPolicyUrl) {
        this.privacyPolicyUrl = generalConfig.privacyPolicyUrl;
      }
      if (generalConfig.showTypeInTable !== undefined) {
        this.showTypeInTable = generalConfig.showTypeInTable;
      }
      if (generalConfig.splitApplicationForm !== undefined) {
        this.splitApplicationForm = generalConfig.splitApplicationForm;
      }
      if (generalConfig.showSalutationField !== undefined) {
        this.showSalutationField = generalConfig.showSalutationField;
      }
      this.updateRules();
      if (generalConfig.groupedByOptions.length > 0) {
        this.groupedBy = generalConfig.groupedByOptions[0];
        this.groupedByOptions = [];
        generalConfig.groupedByOptions.forEach(values => {
          this.translate.get(values).subscribe(labels => {
            const labelList = [];
            for (let key in labels) {
              labelList.push(labels[key]);
            }
            this.groupedByOptions.push({label: labelList.join(' | '), value: values});
          });
        });
      }
      this.groupedTimetable = this.splitToGroupedEntries(this.timetableEntries);
      if (this.groupedTimetable.length != this.groupedTimetableExpanded.length) {
        this.groupedTimetableExpanded = [];
        for (let i = 0; i < this.groupedTimetable.length; i++) {
          this.groupedTimetableExpanded[i] = false;
        }
      }
      this.updateNoteNumbering();

      this.tagCategories = tagCategories;
      tags.forEach(tag=> this.tags[tag.id] = tag);
    });
  }

  hasVisibleText(text: string): boolean {
    const checkElement = document.createElement("div");
    checkElement.innerHTML = text;

    if (checkElement.innerText.trim() == "") {
      return false;
    }
    return true;
  }

  toggleSelection(rowEntry: RowEntry) {
    if (!this.canSelectEntry(rowEntry)) {
      return;
    }
    if (rowEntry.entry.type != 'BREAK') {
      rowEntry.selected = !rowEntry.selected;
    }
    this.updateRules();
  }

  stopEvent(event: Event) {
    event.stopPropagation();
  }

  updateRules() {
    const typeCount: {[key: string]: number} = {};
    this.timetableEntries
      .filter(row => row.selected)
      .forEach(row => {
        if (row.entry.type in typeCount) {
          typeCount[row.entry.type] += 1;
        } else {
          typeCount[row.entry.type] = 1;
        }
      });
    this.rules.forEach(r => r.validate(typeCount));
  }

  submit() {
    if (this.requestRunning) {
      return;
    }
    this.requestRunning = true;
    const data = {
      email: this.email,
      emailRepeated: this.emailRepeated,
      salutation: this.salutation,
      firstname: this.firstname,
      lastname: this.lastname,
      timetable: this.timetableEntries.filter(re => re.selected).map(re => re.entry.id),
      additionalFieldData: this.additionalFields.map(field => field.toJson())
    };
    this.http.post(
      `${environment.url}${this.adminService.tenant}/application`,
      data
    ).subscribe({
      next: () => {
        this.errors = undefined;
        this.applicationSuccessful = true;
        this.requestRunning = false;
      },
      error: err => {
        this.applicationSuccessful = false;
        if ('error' in err && typeof err.error == 'object' && 'type' in err.error && err.error.type === 'UserErrors') {
          this.errors = err.error;
        } else {
          const message: any = {
            type: "PlainErrorMessage",
            summary: "Fehler bei der Kommunikation mit dem Backend",
            detail: err.message
          };
          this.errors = {
            type: "UserErrors",
            messages: [message]
          };
          console.log(err);
        }
        this.updateData();
        this.requestRunning = false;
      }
    });
  }

  private isBeforeToday(input: Date): boolean {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return today.valueOf() > input.valueOf();
  }

  private isAfterToday(input: Date): boolean {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return today.valueOf() < input.valueOf();
  }

  isOverbooked(i: RowEntry): boolean {
    return i.entry.type != 'BREAK'
      && (!!i.entry.attendeeCount)
      && i.entry.attendeeCount >= i.entry.maxAttendees
      && i.entry.maxAttendees != 0;
  }

  canSelectEntry(i: RowEntry): boolean {
    return i.entry.type != 'BREAK'
      && i.entry.attendeeCount != undefined
      && (i.entry.attendeeCount < i.entry.maxAttendees || i.entry.maxAttendees == 0);
  }

  changeGrouping(groupedBy: string[]) {
    this.groupedBy = groupedBy;
    this.filter();
  }

  splitToGroupedEntries(entries: RowEntry[]): GroupedEntry[] {
    let table: GroupedEntry[] = [];

    for (let rowEntry of entries) {
      const groupedByValues = this.getGroupedByValues(rowEntry);
      // If any value has an unexpected type all entries will be displayed in one table without grouping.
      if (groupedByValues.find(value => this.groupedByType(value) === 'unknown')) {
        return [new GroupedEntry(undefined, entries)];
      }
      if (!table.some((tableEntry) => this.groupedByArrayEquals(tableEntry.groupedBy, groupedByValues))) {
        table.push(
          new GroupedEntry(
            groupedByValues,
            entries.filter((entry) => this.groupedByArrayEquals(this.getGroupedByValues(entry), groupedByValues))
          )
        );
      }
    }

    table.forEach(daily => {
      let timeslotCount = 0;
      let timeslotId: number = -1;
      daily.entries.map(value => {
        if (timeslotId === -1 || timeslotId !== value.timeslot.id) {
          timeslotId = value.timeslot.id;
          timeslotCount++;
        }
        value.timeslotCount = timeslotCount
        return value;
      });
    });

    return table.sort((a, b) => this.groupedByArrayCompare(a.groupedBy, b.groupedBy));
  }

  /**
   * Returns the type of the value that the entries should be grouped by.
   * Expected types are string, Date, Time and null/undefined.
   * Returns unknown if the value has another type.
   */
  groupedByType(groupedBy: any): 'string' | 'Timeslot' | 'Date' | 'Time' | 'undefined' | 'unknown' {
    if (groupedBy === undefined || groupedBy === null) {
      return 'undefined';
    }
    if (typeof groupedBy === 'string') {
      if (groupedBy.trim() === '') {
        return 'undefined';
      }
      return 'string';
    }
    if (groupedBy instanceof Timeslot) {
      return 'Timeslot';
    }
    if (groupedBy instanceof Date) {
      return 'Date';
    }
    if ('hours' in groupedBy && 'minutes' in groupedBy) {
      return 'Time';
    }
    return 'unknown';
  }

  groupedByArrayEquals(a: any[], b: any[]) {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!this.groupedByEquals(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }

  groupedByEquals(a: any, b: any): boolean {
    if (this.groupedByType(a) !== this.groupedByType(b)) {
      return false;
    }
    switch (this.groupedByType(a)) {
      case 'string':
        return a === b;
      case 'Timeslot':
        return a.id === b.id;
      case 'Date':
        return a.toString() === b.toString();
      case 'Time':
        return a.hours === b.hours && a.minutes === b.minutes;
      case 'undefined':
        return true;
      default:
        return a === b;
    }
  }

  groupedByArrayCompare(a: any[], b: any[]): number {
    if (a.length !== b.length) {
      return NaN;
    }
    for (let i = 0; i < a.length; i++) {
      let result = this.groupedByCompare(a[i], b[i]);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  }

  groupedByCompare(a: any, b: any): number {
    if (this.groupedByType(a) === 'undefined' && this.groupedByType(b) !== 'undefined') {
      return -1;
    }
    if (this.groupedByType(a) !== 'undefined' && this.groupedByType(b) === 'undefined') {
      return 1;
    }
    if (this.groupedByType(a) !== this.groupedByType(b)) {
      return NaN;
    }
    switch (this.groupedByType(a)) {
      case 'string':
        return a.localeCompare(b);
      case 'Timeslot':
        return compareTimeslotStartEnd(a, b);
      case 'Date':
        return a.valueOf() - b.valueOf();
      case 'Time':
        return a.hours - b.hours !== 0 ? a.hours - b.hours : a.minutes - b.minutes;
      case 'undefined':
        return 0;
      default:
        return NaN;
    }
  }

  getGroupedByValues(entry: RowEntry): (string | Date | Time | undefined)[] {
    let groupedByValues: any[] = [];
    this.groupedBy.forEach((element, i) => {
      const splitGroupedBy = element.split('.');
      groupedByValues[i] = entry;
      for (let key of splitGroupedBy) {
        groupedByValues[i] = groupedByValues[i] ? groupedByValues[i][key] : undefined;
      }
    });
    return groupedByValues;
  }

  get selectedTimetableEntries(): RowEntry[] {
    return this.timetableEntries.filter(entry => entry.selected);
  }

  noteNumberMap: Map<number, number> = new Map();
  entryNotes: {number: number, text: string}[] = [];

  updateNoteNumbering() {
    this.noteNumberMap = new Map();
    this.entryNotes = [];
    let noteCounter = 0;
    this.timetableEntries.forEach(entry => {
      const note = entry.entry.notes;
      if (note && note.trim().length > 0) {
        noteCounter++;
        this.noteNumberMap.set(entry.entry.id, noteCounter);
        this.entryNotes.push({number: noteCounter, text: note});
      }
    });
  }

  linkFormat(link: string): string {
    if (link.startsWith('https://') || link.startsWith('http://')) {
      return link;
    } else {
      return 'https://' + link;
    }
  }

  filter() {
    let filteredEntries = this.timetableEntries;
    this.tagCategories.forEach(category => {
      if (this.selectedTags[category.id] && this.selectedTags[category.id].length > 0) {
        filteredEntries = filteredEntries.filter(value =>value.entry.tagIds.some(id => this.selectedTags[category.id].find(tag => tag === id)));
      }
    });
    filteredEntries = filteredEntries.filter(value =>
      value.entry.title.toLocaleLowerCase().indexOf(this.searchTerm.toLocaleLowerCase()) !== -1 ||
      (value.entry.subtitle && value.entry.subtitle.toLocaleLowerCase().indexOf(this.searchTerm.toLocaleLowerCase()) !== -1)
    )
    this.groupedTimetable = this.splitToGroupedEntries(filteredEntries);
    this.updateNoteNumbering();
  }

  selectTag(tagId: number) {
    let tag = this.tags[tagId];
    if(!this.selectedTags[tag.categoryId]){
      this.selectedTags[tag.categoryId] = [];
    }
    if (this.selectedTags[tag.categoryId].find(t => t === tagId)) {
      this.selectedTags[tag.categoryId] = this.selectedTags[tag.categoryId].filter(t => t !== tagId)
    } else {
      this.selectedTags[tag.categoryId].push(tagId);
    }
    this.filter();
  }

  removeAllFilters(){
    this.tagCategories.forEach(category=>{
      this.selectedTags[category.id] = [];
    });
    this.filter();
  }

  get allSelectedTags(): number[]  {
    let tags: number[] = [];
    this.tagCategories.forEach(category => {
      if (this.selectedTags[category.id]) {
        tags.push(...this.selectedTags[category.id]);
      }
    });
    return tags;
  }
  
  goToPage0() {
    this.page = 0;
    // Timeout, Angular muss die Seite erst aktualisieren
    setTimeout(() => {
      const element = document.getElementById('kontaktdaten');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Weiter scrollen, damit Header nicht vom Control Panel verdeckt wird
        window.scrollBy(0, -70);
      }
    }, 100);
  }

  goToPage1() {
    this.page = 1;
    // Timeout, Angular muss die Seite erst aktualisieren
    setTimeout(() => {
      const element = document.getElementById('veranstaltungen');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Weiter scrollen, damit Header nicht vom Control Panel verdeckt wird
        window.scrollBy(0, -70);
      }
    }, 100);
  }
}

class GroupedEntry {

  groupedBy: (Date | Time | Timeslot | string)[];
  entries: RowEntryTimeslotCount[];

  constructor(groupedBy: any, entries: RowEntryTimeslotCount[]) {
    this.groupedBy = groupedBy;
    this.entries = entries;
  }
}

class Rule {

  text: string;
  min?: number;
  max?: number;
  targetType: string;
  validated = false;

  constructor(text: string, targetType: string, min?: number, max?: number) {
    this.text = text;
    this.targetType = targetType;
    this.min = min;
    this.max = max;
  }

  validate(typeCount: {[key: string]: number}) {
    let count = 0;
    if (this.targetType in typeCount) {
      count = typeCount[this.targetType];
    }

    let result = true;
    if (this.max && count > this.max) {
      result = false;
    }
    if (this.min && count < this.min) {
      result = false;
    }

    this.validated = result;
  }
}
